import * as angular from 'angular';
import { Observable, combineLatest as latest } from 'rxjs';
import { combineLatest, filter, first, map, shareReplay, takeWhile, withLatestFrom } from 'rxjs/operators';

import { ReportTypes } from '@mode/shared/contract-common';
import { getCsrfToken, isPresent } from '@mode/shared/util-js';
import { AppStore } from '~/types/main.types';

import { ReportViewerFacade } from '@mode/report-viewer/domain';
import { ReportParameterOptionsFacade } from '@mode/reports/domain';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { ReportRunFormField } from 'libs/shared/contract-common/src/lib/report';
import { reportContextSelector, selectedReportWebReportRunsLinkSelector } from '../../reducers/selectors';
import { SelectedReportSelectors } from '../../selectors/selected-report.selectors';
import { FeatureToggleService } from '../../services/feature-toggle-service';
import template from './report-parameters.html';

/* eslint-disable @typescript-eslint/no-explicit-any */
export class ReportParametersController {
  static $inject = [
    '$element',
    'RxStoreService',
    'FeatureToggleService',
    '$window',
    'ReportViewerFacade',
    'ReportParameterOptionsFacade',
    'AnalyticsService',
  ];

  public formFields$: Observable<ReportTypes.ReportRunFormField[]>;
  public reportParameterOptionsLoading$: Observable<boolean>;
  public reportParameterOptionsComplete$: Observable<boolean>;
  public showRunButton$: Observable<boolean>;
  public manualRunDisabled$: Observable<boolean>;
  public parametersEnabled$: Observable<boolean>;
  public expectedRuntime$: Observable<number>;

  public parametersFeatureToggleAvailable = false;
  private isDisposed: boolean;
  private hiddenParametersMap: Map<string, Set<string>> = new Map<string, Set<string>>();
  private parameterOptionCount: Record<string, number> = {};
  private parameterOptionFieldType: Record<string, ReportTypes.ReportRunFormFieldType> = {};

  constructor(
    public $element: angular.IRootElementService,
    public rxStoreService: AppStore,
    public featureToggleService: FeatureToggleService,
    private $window: angular.IWindowService,
    private reportViewerFacade: ReportViewerFacade,
    private reportParameterOptionsFacade: ReportParameterOptionsFacade,
    private analyticsService: AnalyticsService
  ) {
    this.isDisposed = false;
    this.formFields$ = latest([
      this.reportViewerFacade.reportRunFormFields$,
      this.reportParameterOptionsFacade.reportParameterOptions$,
      this.reportViewerFacade.areReportRunFormFieldsDynamic$,
    ]).pipe(
      map(([reportRunFormFields, reportParameterOptions, areDynamic]) => {
        // For legacy flow, or when there are no dynamic multiselect form fields, we do not need to fetch options separately
        if (!areDynamic) {
          return reportRunFormFields;
        }
        const fields: ReportRunFormField[] = [...reportRunFormFields];
        reportParameterOptions.forEach((parameterOptions) => {
          const fieldIndex = fields.findIndex((f) => f.name === parameterOptions.name);
          if (fieldIndex !== undefined && fieldIndex >= 0) {
            fields[fieldIndex] = {
              ...fields[fieldIndex],
              options: parameterOptions.options,
              source_size: parameterOptions.sourceSize,
              options_error: parameterOptions.optionsError,
            };
          }
        });
        return fields;
      }),
      shareReplay(1),
      takeWhile(() => !this.isDisposed)
    );

    this.formFields$.pipe(takeWhile(() => !this.isDisposed)).subscribe((formFields) => {
      formFields.forEach((f) => {
        this.parameterOptionCount[f.name] = f.options.length;
        this.parameterOptionFieldType[f.name] = f.type;
      });
    });

    this.reportParameterOptionsLoading$ = this.reportParameterOptionsFacade.reportParameterOptionsLoading$;
    this.reportParameterOptionsComplete$ = this.reportParameterOptionsFacade.reportParameterOptionsComplete$;
    this.manualRunDisabled$ = this.reportViewerFacade.manualRunDisabled$;
    this.expectedRuntime$ = this.reportViewerFacade.expectedRuntime$;

    this.showRunButton$ = featureToggleService.featureParameterRunButtonEnabled$;
    this.parametersEnabled$ = featureToggleService.featureParametersEnabled$;
    this.parametersEnabled$
      .pipe(
        combineLatest(this.rxStoreService.select(reportContextSelector)),
        takeWhile(() => !this.parametersFeatureToggleAvailable)
      )
      .subscribe(([parametersEnabled, reportContext]) => {
        if (reportContext != null && typeof parametersEnabled === 'boolean') {
          this.parametersFeatureToggleAvailable = true;
        }
      });

    latest([
      this.reportViewerFacade.reportAccountUsername$,
      this.reportViewerFacade.token$,
      this.reportViewerFacade.reportRunToken$,
      this.reportViewerFacade.areReportRunFormFieldsDynamic$,
    ])
      .pipe(takeWhile(() => !this.isDisposed))

      .subscribe(([userName, reportToken, runToken, areDynamic]) => {
        if (areDynamic) {
          // For legacy flow, or when there are no dynamic multiselect form fields, we do not need to fetch options separately
          this.reportParameterOptionsFacade.loadReportParameterOptions(userName, reportToken, runToken);
        }
      });
  }

  $onInit() {
    this.isDisposed = false;
  }

  $postLink() {
    this.rxStoreService
      .select(selectedReportWebReportRunsLinkSelector)
      .pipe(
        withLatestFrom(this.manualRunDisabled$, this.rxStoreService.select(SelectedReportSelectors.getReportRunToken)),
        takeWhile(() => !this.isDisposed)
      )
      .subscribe(([url, manualRunDisabled, reportRunToken]) => {
        if (typeof manualRunDisabled === 'boolean' && !manualRunDisabled) {
          this.$element.find("form[name='utf8']").val('\u2713');
          this.$element.find("form[name='report-form']").attr('action', url);
          this.$element.find("form[name='report-form'] > input[name='last_run_id']").val(reportRunToken);
          this.$element
            .find("form[name='report-form'] > input[name='authenticity_token']")
            .val(getCsrfToken(this.$window.document));
        }
      });
  }

  $onDestroy() {
    this.isDisposed = true;
  }

  updateFormParameters(changes: [string | number, boolean][], parameterName: string, isSingleSelect = false) {
    for (const change of changes) {
      const [parameterValue, isSelected] = change;
      if (!this.hiddenParametersMap.has(parameterName)) {
        this.hiddenParametersMap.set(parameterName, new Set());
      }
      const parameterValues = this.hiddenParametersMap.get(parameterName);
      if (isSelected && isSingleSelect) {
        parameterValues.clear();
        parameterValues.add(parameterValue?.toString());
      }

      if (!isSingleSelect) {
        if (isSelected) {
          parameterValues.add(parameterValue?.toString());
        } else {
          parameterValues.delete(parameterValue?.toString());
        }
      }
    }
  }

  handleRunClick() {
    this.rxStoreService
      .select(SelectedReportSelectors.getReportRunToken)
      .pipe(filter(isPresent), first())
      .subscribe((runToken) => {
        this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
          location: 'Report View',
          action: 'Run',
          context: 'report_run',
          context_id: runToken,
        });
      });

    const $form = this.$element.find("form[name='report-form']");

    const hiddenParametersHtml = [];
    this.hiddenParametersMap.forEach((values, parameterName) => {
      const formattedName = `report_run[params][${parameterName}][]`;
      if (
        this.parameterOptionFieldType[parameterName] === ReportTypes.ReportRunFormFieldType.Multiselect &&
        this.parameterOptionCount[parameterName] === values.size
      ) {
        hiddenParametersHtml.push(`<input type='hidden' name='${formattedName}' value='all'>`);
      } else {
        values.forEach((val) => {
          // Escape single quotes in value since the string below already uses single quotes
          const escapedVal = (val ?? '').replace(/"/g, '&quot;');
          hiddenParametersHtml.push(`<input type='hidden' name='${formattedName}' value="${escapedVal}">`);
        });
      }
    });
    $form.append(hiddenParametersHtml.join());

    // still using vanilla form submission because the state of parameters is still stored in html form inputs
    // when this is migrated to the state we can move towards a more ajax report run style
    $form.submit();
  }
}

const reportParametersComponent: angular.IComponentOptions = {
  bindings: {},
  template: template,
  controller: ReportParametersController,
  controllerAs: 'ctrl',
};

angular.module('mode.components').component('reportParameters', reportParametersComponent);
